import { guestPrefsHeaders, isGuestHint, setGuestPrefs } from './guest-prefs';
export interface PluginInfo {
  id: string;
  name: string;
  site: string;
  version: string;
  lang: string;
  hasFilters: boolean;
  hasPreferences: boolean;
  contentType: 'novel' | 'manga' | 'anime';
  tagsMode: 'solo' | 'multiple';
  icon?: string;
}

export interface FilterOption {
  label: string;
  value: string;
}

export interface FilterDef {
  type: 'Text' | 'Picker' | 'Checkbox' | 'Switch' | 'XCheckbox' | 'Range';
  label: string;
  value: unknown;
  options?: FilterOption[];
  /** Лише для type === 'Range': межі повзунка (value — кортеж [min, max]). */
  min?: number;
  max?: number;
  step?: number;
  /** Лише для type === 'Picker': примусово одиничний вибір навіть якщо
   * плагін загалом tagsMode === 'multiple' (напр. сортування/напрямок). */
  solo?: boolean;
}

export type FiltersDef = Record<string, FilterDef>;

export interface NovelItem {
  name: string;
  path: string;
  cover?: string;
}

export interface ChapterItem {
  name: string;
  path: string;
  releaseTime?: string | null;
  chapterNumber?: number;
  scanlator?: string | string[];
  /** Ідентифікатор групи перекладу/озвучення (лише для contentType==='anime') — див. SourceNovel.groups. */
  group?: string;
  /** Лише smakolykytl: номер/назва тому, номер розділу в томі й власна назва розділу (для заголовків). */
  volumeNumber?: number;
  volumeShown?: boolean;
  volumeKey?: string;
  volumeName?: string;
  chapterRank?: string;
  chapterTitle?: string;
}

/** Результат /api/video/resolve — прямий (звичайний <video>/hls.js) чи
 * фолбековий (<iframe>) спосіб відтворити посилання на чужий плеєр
 * (ashdi.vip/tortuga.tw/mp4upload.com/...), див. src/lib/video на сервері. */
export interface VideoResolveResult {
  status: 'ok' | 'unsupported' | 'error';
  extractor: string | null;
  sourceUrl: string;
  kind?: 'hls' | 'mp4' | 'iframe';
  qualities?: Record<string, string>;
  defaultQuality?: string | null;
  subtitle?: string | null;
  message?: string;
}

export interface SourceNovel extends NovelItem {
  id?: string;
  pluginId?: string;
  inLibrary?: boolean;
  genres?: string;
  summary?: string;
  author?: string;
  artist?: string;
  status?: string;
  /** Країна походження (smakolykytl). */
  country?: string;
  /** Перекладач / команда (smakolykytl). */
  translator?: string;
  /** Стан перекладу так, як його віддає сайт, напр. "Триває" (smakolykytl). */
  translationStatus?: string;
  /** Статус написання оригіналу (smakolykytl). */
  writingStatus?: string;
  /** Інші назви твору (smakolykytl). */
  altNames?: string;
  /** Рік випуску (smakolykytl). */
  year?: string;
  chapters?: ChapterItem[];
  url?: string;
  readChapters?: string[];
  /** true, якщо джерело зараз недоступне і показані розділи — не свіжі (порожньо), а тільки збережені дані. */
  chaptersFailed?: boolean;
  /** Групи перекладу/озвучення для 2-крокового вибору (лише contentType==='anime'). */
  groups?: { id: string; type: string; team: string }[];
  /** "Ролі озвучували" — ЛИШЕ якщо сайт поділяє акторів по командах
   * (лише contentType==='anime'); якщо поділу нема — відсутнє, розділ не показуємо. */
  voiceCastGroups?: { team: string; actors: string[] }[];
  /** Рік виходу (лише contentType==='anime'). */
  releaseYear?: string;
  /** Кількість серій (лише contentType==='anime'), напр. "12 з 12 серій" — окремо від року й тривалості. */
  episodesLabel?: string;
  /** Тривалість однієї серії (лише contentType==='anime'), напр. "22 хв.". */
  episodeDuration?: string;
  /** "Пов'язані твори" (лише contentType==='anime', якщо сайт має такий блок). */
  related?: { name: string; path: string; cover?: string }[];
}

export interface LibraryItem {
  id: string;
  pluginId: string;
  path: string;
  name: string;
  cover?: string;
  addedAt: string;
  chapterCount: number;
  unreadCount: number;
  categoryIds: string[];
  /** Тип контенту джерела — визначається один раз при додаванні (за типом
   * ДЖЕРЕЛА). Кожен тип має власну незалежну бібліотеку з власними категоріями. */
  contentType: 'manga' | 'novel' | 'anime';
  sortOrder?: 'asc' | 'desc';
  /** Знімок даних твору (усе, КРІМ розділів), збережений при додаванні в бібліотеку. */
  cachedNovel?: Omit<SourceNovel, 'chapters'>;
  /** Обраний переклад (для contentType==='anime') — див. коментар у src/types.ts. */
  animeSelection?: { type: string; team: string };
  /** Лише pluginId==='anitube': номер останньої переглянутої серії (з назви, не порядковий) — див. коментар у src/types.ts. unreadCount уже рахує з урахуванням цього поля на сервері. */
  lastWatchedEpisodeNumber?: number;
}

export interface AnitubeStatus {
  loggedIn: boolean;
  username?: string;
  avatarUrl?: string;
  loggedInAt?: string;
  lastSyncAt?: string | null;
}

export interface KotatsuImportItemResult {
  title: string;
  pluginId: string;
  status: 'added' | 'updated' | 'error';
  message?: string;
  readChapters?: number;
  totalChapters?: number;
}

export interface KotatsuImportSummary {
  categoriesCreated: string[];
  matched: number;
  skippedOtherSource: number;
  items: KotatsuImportItemResult[];
}
export interface Category {
  id: string;
  name: string;
  order: number;
  checkUpdates: boolean;
  /** Тип контенту власної бібліотеки, якій належить категорія. */
  contentType: 'manga' | 'novel' | 'anime';
}

export interface HistoryItem {
  novelId: string;
  pluginId: string;
  novelPath: string;
  novelName: string;
  cover?: string;
  chapterPath: string;
  chapterName: string;
  chapterNumber?: number;
  progress: number;
  page?: number;
  totalPages?: number;
  /** Позиція відео в секундах на момент паузи (лише anime) — для кнопки "Продовжити". */
  positionSeconds?: number;
  /** Загальна тривалість відео в секундах (лише anime), якщо відома. */
  durationSeconds?: number;
  updatedAt: string;
  /** Сервер: чи позначено саме цей розділ/серію прочитаним, і скільки розділів твору позначено. */
  chapterRead?: boolean;
  readCount?: number;
}

export interface UpdateItem {
  novelId: string;
  pluginId: string;
  novelPath: string;
  novelName: string;
  cover?: string;
  chapterPath: string;
  chapterName: string;
  chapterNumber?: number;
  checkedAt: string;
  read: boolean;
}

export interface RedactRule {
  id: string;
  from: string;
  to: string;
  scope: 'chapter' | 'novel';
  chapterPath?: string;
  chapterName?: string;
  createdAt: string;
}

export interface RecommendationItem {
  id: string;
  fromUsername: string;
  toUsername: string;
  novelId: string;
  pluginId: string;
  novelPath: string;
  novelName: string;
  cover?: string;
  chapterCount: number;
  createdAt: string;
  read: boolean;
}

export interface RecommendTarget {
  username: string;
  /** 'seen' — твір вже є в бібліотеці друга; 'sent' — я вже рекомендував (і друг ще не видалив); 'none' — можна рекомендувати. */
  status: 'seen' | 'sent' | 'none';
}

async function req<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...guestPrefsHeaders(url), ...(init?.headers ?? {}) },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Помилка запиту (${res.status})`);
  }
  return res.json();
}

export const api = {
  login: (username: string, password: string) =>
    req<{ ok: boolean; username: string; role?: string }>('/api/auth/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  /** Самореєстрація гостьового акаунта (роль задає сервер). Після успіху користувач уже увійшов. */
  register: (email: string, nickname: string, password: string, confirm: string) =>
    req<{ ok: boolean; username: string; role: string }>('/api/auth/register', { method: 'POST', body: JSON.stringify({ email, nickname, password, confirm }) }),
  forgotPassword: (email: string) =>
    req<{ ok: boolean; message: string }>('/api/auth/forgot', { method: 'POST', body: JSON.stringify({ email }) }),
  /** Перевіряє код відновлення, НЕ витрачаючи його (потім resetPassword із тим самим кодом). */
  checkResetCode: (email: string, code: string) =>
    req<{ ok: boolean }>('/api/auth/reset/check', { method: 'POST', body: JSON.stringify({ email, code }) }),
  resetPassword: (email: string, code: string, password: string, confirm: string) =>
    req<{ ok: boolean }>('/api/auth/reset', { method: 'POST', body: JSON.stringify({ email, code, password, confirm }) }),
  /** Зміна нікнейма (лише гостьовий акаунт); потрібен поточний пароль. */
  changeNickname: (nickname: string, password: string) =>
    req<{ ok: boolean; nickname: string }>('/api/auth/nickname', { method: 'POST', body: JSON.stringify({ nickname, password }) }),
  /** Зміна пароля (лише гостьовий акаунт); потрібен поточний пароль. Інші пристрої буде розлогінено. */
  changePassword: (currentPassword: string, password: string, confirm: string) =>
    req<{ ok: boolean }>('/api/auth/change-password', { method: 'POST', body: JSON.stringify({ currentPassword, password, confirm }) }),
  /** Зміна пошти (лише гостьовий акаунт); потрібен поточний пароль. */
  changeEmail: (email: string, password: string) =>
    req<{ ok: boolean }>('/api/auth/change-email', { method: 'POST', body: JSON.stringify({ email, password }) }),
  /** Видалення власного акаунта (лише гостьовий); потрібен поточний пароль. */
  deleteAccount: (password: string) =>
    req<{ ok: boolean }>('/api/auth/delete-account', { method: 'POST', body: JSON.stringify({ password }) }),
  telegramLink: () => req<{ ok: boolean; url: string; expiresInMin: number }>('/api/auth/telegram/link', { method: 'POST' }),
  telegramUnlink: (password: string) =>
    req<{ ok: boolean }>('/api/auth/telegram/unlink', { method: 'POST', body: JSON.stringify({ password }) }),
  logout: () => req<{ ok: boolean }>('/api/auth/logout', { method: 'POST' }),
  forgetEverything: () => req<{ ok: boolean }>('/api/auth/forget-everything', { method: 'POST' }),
  me: () => req<{ username: string; role?: string; nickname?: string; telegramLinked?: boolean; telegramAvailable?: boolean }>('/api/auth/me'),

  plugins: () => req<PluginInfo[]>('/api/plugins'),
  popular: (pluginId: string, page = 1, latest = false, filters?: Record<string, unknown>) =>
    req<NovelItem[]>(
      `/api/plugins/${pluginId}/popular?page=${page}&latest=${latest ? 1 : 0}${
        filters ? `&filters=${encodeURIComponent(JSON.stringify(filters))}` : ''
      }`,
    ),
  pluginFilters: (pluginId: string) => req<{ site: string; filters: FiltersDef }>(`/api/plugins/${pluginId}/filters`),
  pluginPreferences: (pluginId: string) =>
    req<{ preferences: FiltersDef; values: Record<string, unknown> }>(`/api/plugins/${pluginId}/preferences`),
  savePluginPreferences: (pluginId: string, values: Record<string, unknown>) => {
    // Гість не має акаунта — його налаштування джерела лишаються в localStorage браузера
    // (guest-prefs.ts) і їдуть із запитами заголовком; залогінений зберігає їх на сервері.
    if (isGuestHint()) {
      setGuestPrefs(pluginId, values);
      return Promise.resolve({ ok: true });
    }
    return req<{ ok: boolean }>(`/api/plugins/${pluginId}/preferences`, { method: 'POST', body: JSON.stringify({ values }) });
  },
  search: (pluginId: string, q: string, page = 1) =>
    req<NovelItem[]>(`/api/plugins/${pluginId}/search?q=${encodeURIComponent(q)}&page=${page}`),
  /** Крос-джерельний пошук — одразу по всіх (або лише novel/manga) підключених джерелах. */
  searchAll: (q: string, contentType: 'novel' | 'manga' | 'anime' | '', page = 1) =>
    req<(NovelItem & { pluginId: string; pluginName: string })[]>(
      `/api/plugins/search?q=${encodeURIComponent(q)}&page=${page}${contentType ? `&contentType=${contentType}` : ''}`,
    ),
  novel: (pluginId: string, novelPath: string) =>
    req<SourceNovel>(`/api/plugins/${pluginId}/novel?path=${encodeURIComponent(novelPath)}`),
  /**
   * Метадані твору + СПИСОК РОЗДІЛІВ зі спільного кешу — миттєво, без запиту
   * до джерела. Може повернути novel: null (ще ніхто цей твір не відкривав)
   * і/або chapters: null (список іще ніхто не завантажував живим запитом).
   * Список може бути ЗАСТАРІЛИМ — це лише "останній відомий стан" для
   * миттєвого показу, поки паралельно йде живий запит api.novel().
   */
  novelCached: (pluginId: string, novelPath: string) =>
    req<{ novel: Omit<SourceNovel, 'chapters'> | null; chapters: ChapterItem[] | null; readChapters: string[] }>(
      `/api/plugins/${pluginId}/novel/cached?path=${encodeURIComponent(novelPath)}`,
    ),
  chapter: (pluginId: string, chapterPath: string, novelPath?: string) =>
    req<{ content: string; url?: string }>(
      `/api/plugins/${pluginId}/chapter?path=${encodeURIComponent(chapterPath)}${
        novelPath ? `&novelPath=${encodeURIComponent(novelPath)}` : ''
      }`,
    ),
  /** Сирий HTML manga-розділу зі спільного кешу (без персональних правил редагування) — для миттєвого показу сторінок у читалці, поки йде живий запит api.chapter(). content: null, якщо в кеші ще нічого немає. */
  /** Прогрес завантаження розділу на диск сервера (джерела з локальним сховищем, зараз mangarama): скільки сторінок збережено з скількох. active=false — зараз нічого не завантажується. */
  chapterProgress: (pluginId: string, chapterPath: string) =>
    req<{ active: boolean; done: number; total: number }>(`/api/plugins/${pluginId}/chapter/progress?path=${encodeURIComponent(chapterPath)}`),
  chapterCached: (pluginId: string, chapterPath: string) =>
    req<{ content: string | null }>(`/api/plugins/${pluginId}/chapter/cached?path=${encodeURIComponent(chapterPath)}`),

  /** Резолвить посилання на чужий плеєр (джерело серії anime) у пряме
   * посилання на відео (m3u8/mp4), уже загорнуте у стрім-проксі сервера. */
  resolveVideo: (url: string) => req<VideoResolveResult>('/api/video/resolve', { method: 'POST', body: JSON.stringify({ url }) }),

  library: () => req<LibraryItem[]>('/api/library'),
  addToLibrary: (data: { pluginId: string; path: string; name: string; cover?: string; animeSelection?: { type: string; team: string } }) =>
    req<{ ok: boolean; id: string }>('/api/library', { method: 'POST', body: JSON.stringify(data) }),
  removeFromLibrary: (id: string) => req<{ ok: boolean }>(`/api/library/${id}`, { method: 'DELETE' }),
  clearLibrary: () => req<{ ok: boolean }>('/api/library', { method: 'DELETE' }),
  /**
   * Повний бекап персональних даних для перенесення на інший сервер чи
   * відновлення після втрати диска (src/routes/library.ts: /export/backup,
   * /import/backup) — на відміну від importBackup нижче, це "наш власний"
   * формат (усе, крім push-підписок і anitube-акаунта — вони прив'язані до
   * КОНКРЕТНОГО сервера/пристрою, докладніше в докстрінгу db.exportBackup),
   * і імпорт ПОВНІСТЮ ЗАМІНЮЄ поточні дані, а не домержовує в них.
   */
  exportBackupUrl: '/api/library/export/backup',
  importAppBackup: async (file: File): Promise<{ ok: boolean; library: number; categories: number }> => {
    const res = await fetch('/api/library/import/backup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Помилка запиту (${res.status})`);
    }
    return res.json();
  },
  /** Міняє обраний переклад (озвучення/субтитри + команда) вже доданого anime-твору. */
  setAnimeSelection: (libraryId: string, animeSelection: { type: string; team: string }) =>
    req<{ ok: boolean }>(`/api/library/${libraryId}`, { method: 'PATCH', body: JSON.stringify({ animeSelection }) }),
  /**
   * Спільна реалізація для обох імпортерів бекапів (endpoint відрізняється —
   * kotatsu чи mihon, формат тіла запиту той самий: сирі байти файлу).
   */
  importBackup: async (
    endpoint: 'kotatsu' | 'mihon',
    file: File,
    onProgress?: (done: number, total: number) => void,
    opts: { quick?: boolean; onSyncProgress?: (done: number, total: number) => void; onSyncStart?: () => void } = {},
  ): Promise<KotatsuImportSummary> => {
    const res = await fetch(`/api/library/import/${endpoint}${opts.quick ? '?quick=1' : ''}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file,
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Помилка запиту (${res.status})`);
    }
    // Сервер стрімить ndjson: {type:'progress',...} по ходу додавання,
    // {type:'done', summary} — фінальний результат самого імпорту, і (лише
    // при ?quick=1) додатково {type:'sync-start'} + {type:'sync-progress',...}
    // — це вже перевірка оновлень по всій бібліотеці, що йде одразу після
    // імпорту в тій самій відповіді (src/routes/library.ts).
    if (!res.body) {
      // Фолбек для середовищ без ReadableStream у fetch — беремо як єдиний JSON.
      return res.json();
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let summary: KotatsuImportSummary | null = null;

    const handleLine = (line: string) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const msg = JSON.parse(trimmed);
      if (msg.type === 'progress') onProgress?.(msg.done, msg.total);
      else if (msg.type === 'done') summary = msg.summary;
      else if (msg.type === 'sync-start') opts.onSyncStart?.();
      else if (msg.type === 'sync-progress') opts.onSyncProgress?.(msg.done, msg.total);
      else if (msg.type === 'error') throw new Error(msg.error);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let nlIndex: number;
      while ((nlIndex = buffer.indexOf('\n')) >= 0) {
        handleLine(buffer.slice(0, nlIndex));
        buffer = buffer.slice(nlIndex + 1);
      }
    }
    if (buffer) handleLine(buffer);

    if (!summary) throw new Error('Порожня відповідь сервера під час імпорту');
    return summary;
  },
  /** Миттєвий пошук закешованих даних твору в бібліотеці (без парсингу джерела), 404 якщо не в бібліотеці. */
  libraryLookup: (pluginId: string, path: string) =>
    req<LibraryItem>(`/api/library/lookup?pluginId=${encodeURIComponent(pluginId)}&path=${encodeURIComponent(path)}`),
  updateLibrarySortOrder: (id: string, sortOrder: 'asc' | 'desc') =>
    req<{ ok: boolean }>(`/api/library/${id}`, { method: 'PATCH', body: JSON.stringify({ sortOrder }) }),
  migrateLibrary: (data: { fromId: string; pluginId: string; path: string; name: string; cover?: string; categoryIds?: string[] }) =>
    req<{ ok: boolean; id: string; categoryIds: string[]; contentType: 'novel' | 'manga' | 'anime' }>('/api/library/migrate', {
      method: 'POST',
      body: JSON.stringify(data),
    }),
  assignLibraryCategories: (novelIds: string[], add: string[], remove: string[]) =>
    req<{ ok: boolean }>('/api/library/assign-categories', { method: 'POST', body: JSON.stringify({ novelIds, add, remove }) }),
  /**
   * Акаунт anitube.in.ua ("Більше" → Імпорт): вхід — той самий DLE-логін
   * (cookie), що й у самому Android-застосунку anitube, без токенів. Після
   * входу сервер сам розкладає аніме-бібліотеку по категоріях
   * Переглянуто/Заплановано/Переглядаю/Відкладено/Покинуто; anitubeSync()
   * — ручний повторний повний прогін цього ж розкладу.
   */
  anitubeStatus: () => req<AnitubeStatus>('/api/anitube/status'),
  anitubeLogin: (username: string, password: string) =>
    req<AnitubeStatus>('/api/anitube/login', { method: 'POST', body: JSON.stringify({ username, password }) }),
  anitubeLogout: () => req<{ ok: boolean }>('/api/anitube/logout', { method: 'DELETE' }),
  anitubeSync: () =>
    req<{
      changed: number;
      imported: number;
      removed: number;
      segments: { segment: string; categoryName: string; entries: number; matched: number }[];
      unmatchedSample: { name: string; path: string; animeId: number | null }[];
    }>('/api/anitube/sync', { method: 'POST' }),
  markLibraryRead: (novelIds: string[]) =>
    req<{ ok: boolean }>('/api/library/mark-read', { method: 'POST', body: JSON.stringify({ novelIds }) }),
  markLibraryUnread: (novelIds: string[]) =>
    req<{ ok: boolean }>('/api/library/mark-unread', { method: 'POST', body: JSON.stringify({ novelIds }) }),

  /** Без аргументу — усі категорії; з contentType — лише власної бібліотеки цього типу. */
  categories: (contentType?: 'manga' | 'novel' | 'anime') =>
    req<Category[]>(`/api/categories${contentType ? `?contentType=${contentType}` : ''}`),
  createCategory: (name: string, checkUpdates: boolean, contentType: 'manga' | 'novel' | 'anime') =>
    req<Category>('/api/categories', { method: 'POST', body: JSON.stringify({ name, checkUpdates, contentType }) }),
  updateCategory: (id: string, patch: { name?: string; checkUpdates?: boolean }) =>
    req<Category>(`/api/categories/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  deleteCategory: (id: string) => req<{ ok: boolean }>(`/api/categories/${id}`, { method: 'DELETE' }),
  reorderCategories: (orderedIds: string[]) =>
    req<{ ok: boolean }>('/api/categories/reorder', { method: 'POST', body: JSON.stringify({ orderedIds }) }),

  history: () => req<HistoryItem[]>('/api/history'),
  clearHistory: () => req<{ ok: boolean }>('/api/history', { method: 'DELETE' }),
  removeHistoryItem: (novelId: string) =>
    req<{ ok: boolean }>(`/api/history/${encodeURIComponent(novelId)}`, { method: 'DELETE' }),
  saveProgress: (data: {
    pluginId: string;
    novelPath: string;
    novelName: string;
    cover?: string;
    chapterPath: string;
    chapterName: string;
    chapterNumber?: number;
    progress: number;
    page?: number;
    totalPages?: number;
    positionSeconds?: number;
    durationSeconds?: number;
  }) => req<{ ok: boolean }>('/api/history', { method: 'POST', body: JSON.stringify(data) }),

  updates: () => req<{ items: UpdateItem[]; lastUpdateCheck: string | null }>('/api/updates'),
  /** Прогрес фонової перевірки бібліотеки поточного користувача (скільки його творів уже перевірено). */
  updatesStatus: () => req<{ running: boolean; done: number; total: number }>('/api/updates/status'),
  /** Прибрати записи зі списку «Нове» (свайп «Приховати»). */
  dismissUpdates: (items: { novelId: string; chapterPath: string }[]) =>
    req<{ ok: boolean }>('/api/updates/dismiss', { method: 'POST', body: JSON.stringify({ items }) }),
  checkUpdates: (categoryId?: string) =>
    req<{ ok: boolean; newUpdates: number; items: UpdateItem[]; lastUpdateCheck: string | null }>(
      '/api/updates/check',
      { method: 'POST', body: JSON.stringify(categoryId ? { categoryId } : {}) },
    ),

  markChaptersRead: (data: { pluginId: string; novelPath: string; chapters: { path: string; chapterNumber?: number }[] }) =>
    req<{ ok: boolean }>('/api/chapters/mark-read', { method: 'POST', body: JSON.stringify(data) }),
  markChaptersUnread: (data: { pluginId: string; novelPath: string; chapters: { path: string; chapterNumber?: number }[] }) =>
    req<{ ok: boolean }>('/api/chapters/mark-unread', { method: 'POST', body: JSON.stringify(data) }),

  getRedactRules: (pluginId: string, novelPath: string, chapterPath: string) =>
    req<{ chapterRules: RedactRule[]; novelRules: RedactRule[] }>(
      `/api/redact?pluginId=${encodeURIComponent(pluginId)}&novelPath=${encodeURIComponent(novelPath)}&chapterPath=${encodeURIComponent(chapterPath)}`,
    ),
  addRedactRule: (data: {
    pluginId: string;
    novelPath: string;
    chapterPath?: string;
    chapterName?: string;
    from: string;
    to: string;
    scope: 'chapter' | 'novel';
  }) => req<{ ok: boolean; rule: RedactRule }>('/api/redact', { method: 'POST', body: JSON.stringify(data) }),
  removeRedactRule: (pluginId: string, novelPath: string, id: string) =>
    req<{ ok: boolean }>(
      `/api/redact/${encodeURIComponent(id)}?pluginId=${encodeURIComponent(pluginId)}&novelPath=${encodeURIComponent(novelPath)}`,
      { method: 'DELETE' },
    ),

  // ---------------- друзі + рекомендації (Соціалізація) ----------------

  friends: () => req<{ friends: string[] }>('/api/social/friends'),
  addFriend: (username: string) =>
    req<{ friends: string[] }>('/api/social/friends', { method: 'POST', body: JSON.stringify({ username }) }),
  removeFriend: (username: string) =>
    req<{ friends: string[] }>(`/api/social/friends/${encodeURIComponent(username)}`, { method: 'DELETE' }),

  /** Список друзів зі статусом рекомендації щодо КОНКРЕТНОГО твору — для меню кнопки "Рекомендувати" на сторінці твору. */
  recommendTargets: (pluginId: string, novelPath: string) =>
    req<{ targets: RecommendTarget[] }>(
      `/api/social/recommend-targets?pluginId=${encodeURIComponent(pluginId)}&path=${encodeURIComponent(novelPath)}`,
    ),
  sendRecommendation: (data: {
    toUsername: string;
    pluginId: string;
    path: string;
    name: string;
    cover?: string;
    chapterCount: number;
  }) => req<{ item: RecommendationItem }>('/api/social/recommend', { method: 'POST', body: JSON.stringify(data) }),

  recommendations: () => req<{ items: RecommendationItem[]; unreadCount: number }>('/api/social/recommendations'),
  removeRecommendation: (id: string) => req<{ ok: boolean }>(`/api/social/recommendations/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  markRecommendationRead: (id: string) =>
    req<{ ok: boolean }>(`/api/social/recommendations/${encodeURIComponent(id)}/read`, { method: 'POST' }),

  // ---------------- PWA-сповіщення про нові розділи ----------------

  vapidPublicKey: () => req<{ publicKey: string }>('/api/notifications/vapid-public-key'),
  subscribePush: (subscription: PushSubscriptionJSON) =>
    req<{ ok: boolean }>('/api/notifications/subscribe', { method: 'POST', body: JSON.stringify(subscription) }),
  unsubscribePush: (endpoint: string) =>
    req<{ ok: boolean }>('/api/notifications/unsubscribe', { method: 'POST', body: JSON.stringify({ endpoint }) }),
};
